import { iframeDataBridge } from '../iframe/iframeDataBridge.js';
import { log } from './vueLogger.js';

class AuthTokenService {
	constructor() {}

	/**
	 * Create full API URL
	 * @param {string} endpoint - API endpoint
	 * @returns {string} Full API URL
	 */
	getApiUrl(endpoint) {
		const baseUrl = this.getApiBaseUrl();
		const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
		return `${baseUrl}/${cleanEndpoint}`;
	}

	/**
	 * Get current auth token from iframe context
	 * @param {string} tokenType - Type of token to retrieve ('token', 'tokenSales', 'tokenAssort', 'tokenDoc')
	 * @returns {string|null} Authentication token
	 */
	async getToken(tokenType = 'token') {
		try {
			const context = await this.getContext();
			return context?.user?.[tokenType] || null;
		} catch (error) {
			log('error', 'AuthService', `❌ Failed to get auth token: ${error.message}`);
			return null;
		}
	}

	/**
	 * Get user information from iframe context
	 * @returns {Object|null} User information
	 */
	async getCurrentUser() {
		try {
			return iframeDataBridge.getCurrentUser();
		} catch (error) {
			log('error', 'AuthService', `❌ Failed to get current user: ${error.message}`);
			return null;
		}
	}

	/**
	 * Get full context from iframe, wait until available
	 * @param {number} maxAttempts - Maximum number of attempts to wait
	 * @param {number} delay - Delay between attempts in ms
	 * @returns {Object|null} Full context
	 */
	async getContext(maxAttempts = 30, delay = 1000) {
		for (let attempt = 1; attempt <= maxAttempts; attempt++) {
			try {
				const context = iframeDataBridge.getContext(false);
				if (context && context.user) {
					return context;
				}

				if (attempt < maxAttempts) {
					log('info', 'AuthService', `⏳ Waiting for context... (attempt ${attempt}/${maxAttempts})`);
					await new Promise((resolve) => setTimeout(resolve, delay));
				}
			} catch (error) {
				log('error', 'AuthService', `❌ Failed to get context (attempt ${attempt}): ${error.message}`);
				if (attempt < maxAttempts) {
					await new Promise((resolve) => setTimeout(resolve, delay));
				}
			}
		}

		log('error', 'AuthService', '❌ Failed to get context after maximum attempts');
		return null;
	}

	/**
	 * Check if user is authenticated
	 * @param {string} tokenType - Type of token to check
	 * @returns {boolean} Authentication status
	 */
	async isAuthenticated(tokenType = 'token') {
		const token = await this.getToken(tokenType);
		return !!token;
	}

	/**
	 * Create headers for authenticated API requests
	 * @param {Object} additionalHeaders - Additional headers to merge
	 * @param {string} tokenType - Type of token to use
	 * @returns {Object} Headers object with authentication
	 */
	async getAuthHeaders(additionalHeaders = {}, tokenType = 'token') {
		const token = await this.getToken(tokenType);

		const headers = {
			...additionalHeaders,
		};

		if (token) {
			headers['Token'] = token;
		}

		return headers;
	}

	/**
	 * Make authenticated fetch request
	 * @param {string} url - Request URL
	 * @param {Object} options - Fetch options
	 * @param {string} tokenType - Type of token to use
	 * @returns {Promise<Response>} Fetch response
	 */
	async authenticatedFetch(url, options = {}, tokenType = 'token') {
		const authHeaders = await this.getAuthHeaders(options.headers, tokenType);

		const requestOptions = {
			method: 'GET',
			credentials: 'same-origin',
			...options,
			headers: authHeaders,
		};

		try {
			const response = await fetch(url, requestOptions);

			if (response.status === 401) {
				console.warn('⚠️ Received 401 unauthorized, retrying with fresh token');

				const freshToken = await this.getToken(tokenType);

				if (freshToken) {
					const freshHeaders = await this.getAuthHeaders(options.headers, tokenType);
					const retryOptions = {
						...options,
						headers: freshHeaders,
					};

					return await fetch(url, retryOptions);
				}
			}

			return response;
		} catch (error) {
			log('error', 'AuthService', `❌ Authenticated fetch failed: ${error.message}`);
			throw error;
		}
	}

	/**
	 * Get API base URL with authentication
	 * @returns {string} Base API URL
	 */
	getApiBaseUrl() {
		return import.meta.env.VITE_API_URL || '';
	}

	/**
	 * Create full API URL
	 * @param {string} endpoint - API endpoint
	 * @returns {string} Full API URL
	 */
	getApiUrl(endpoint) {
		const baseUrl = this.getApiBaseUrl();
		const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
		return `${baseUrl}/${cleanEndpoint}`;
	}
}

const authService = new AuthTokenService();
export default authService;

export const getToken = (tokenType) => authService.getToken(tokenType);
export const getCurrentUser = () => authService.getCurrentUser();
export const getContext = () => authService.getContext();
export const isAuthenticated = (tokenType) => authService.isAuthenticated(tokenType);
export const getAuthHeaders = (additionalHeaders, tokenType) => authService.getAuthHeaders(additionalHeaders, tokenType);
export const authenticatedFetch = (url, options, tokenType) => authService.authenticatedFetch(url, options, tokenType);
export const getApiUrl = (endpoint) => authService.getApiUrl(endpoint);
